'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { sortBy } from 'underscore';
import { NavMarker } from 'react-rhumb';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import text from 'I18n/utils/unescapedText';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
import UIButton from 'UIComponents/button/UIButton';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import UITruncateString from 'UIComponents/text/UITruncateString';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import Routes from 'ContentUtils/Routes';
import { landing_page, blog_post } from 'ContentUtils/constants/Categories';
import { PUBLISHED_PAGE, DRAFT_PAGE, SENT_EMAIL, DRAFT_EMAIL, CODED_FILE, CUSTOM_WIDGET as COS_INDEX_CONNECTION_CATEGORY_CUSTOM_WIDGET, BLOG_POST_TEMPLATE, BLOG_LISTING_TEMPLATE, PAGE_TEMPLATE, SYSTEM_TEMPLATE, QUOTE_TEMPLATE, GLOBAL_GROUP, GLOBAL_CONTENT_PARTIAL, EMAIL_TEMPLATE, CONTENT_SETTINGS, BLOG, SALES_QUOTE_TEMPLATE } from 'DesignData/constants/CosIndexConnectionCategories';
import { LAYOUT as COS_INDEX_OBJECT_TYPE_LAYOUT, SITE_SETTINGS, DOMAIN } from 'DesignData/enums/CosIndexObjectTypes';
import { TEMPLATE, LAYOUT, CUSTOM_WIDGET } from 'DesignData/enums/DesignAssetTypes';
import * as RequestStatus from 'DesignData/enums/RequestStatus';
import { FilePropTypes, dependentsByCategory as DependentsByCategoryPropType } from 'DesignData/propTypes/finder';
import { createPath } from 'DesignData/helpers/routingHelper';
import FileResults from 'DesignFinderUI/components/results/FileResults';
import FileResultsEmptyText from 'DesignFinderUI/components/results/FileResultsEmptyText';
import FileResultsList from 'DesignFinderUI/components/results/FileResultsList';
import FileResultsLoading from 'DesignFinderUI/components/results/FileResultsLoading';
import ResultItem from 'DesignFinderUI/components/results/ResultItem';
import withFileDependents from 'DesignFinderUI/hocs/withFileDependents';
import { hideDependents as hideDependentsAction } from 'DesignData/actions/finder/Files';
import { getActiveAssetId } from 'DesignData/selectors/app';
import { getFile } from 'DesignData/selectors/finder';
import FinderWrapper from 'DesignFinderUI/containers/FinderWrapper';
import { compose } from 'redux';

const i18nKey = key => `designManager.finder.FileDependents.${key}`;

class FileDependents extends Component {
  constructor(...args) {
    super(...args);

    this.renderDependentContent = content => {
      const {
        id,
        categoryId,
        name,
        subcategory
      } = content;
      let help = null;

      if (categoryId === landing_page.id || categoryId === blog_post.id) {
        help = /*#__PURE__*/_jsx(UITruncateString, {
          children: content.url || ''
        });
      }

      const renderedTitle = /*#__PURE__*/_jsx(UITruncateString, {
        children: /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: Routes.details(id, categoryId, subcategory),
          children: name
        })
      });

      return /*#__PURE__*/_jsx(ResultItem, {
        help: help,
        nested: true,
        title: renderedTitle
      }, id);
    };

    this.renderDependentFile = (designAssetType, {
      id,
      label
    }) => {
      return /*#__PURE__*/_jsx(UIRouterLink, {
        to: createPath(designAssetType, id),
        children: /*#__PURE__*/_jsx(ResultItem, {
          nested: true,
          title: label
        }, id)
      });
    };

    this.renderDependentCodedFile = ({
      id,
      label
    }) => {
      return this.renderDependentFile(TEMPLATE, {
        id,
        label
      });
    };

    this.renderDependentCustomModule = ({
      id,
      label
    }) => {
      return this.renderDependentFile(CUSTOM_WIDGET, {
        id,
        label
      });
    };

    this.renderDependentTemplate = ({
      id,
      label,
      dependentType
    }) => {
      const designAssetType = dependentType === COS_INDEX_OBJECT_TYPE_LAYOUT ? LAYOUT : TEMPLATE;
      return this.renderDependentFile(designAssetType, {
        id,
        label
      });
    };

    this.renderDependentEmailDragDropTemplate = model => {
      if (model.path.startsWith('EMAIL_DND_TEMPLATE')) {
        const formattedLabel = model.label.replace('.html', '');
        const href = Routes.create('email', null, {
          moduleIdForReviewingDependents: this.props.fileId,
          moduleName: this.props.fileName
        });
        return /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: href,
          className: "p-y-2 display-block",
          children: formattedLabel
        });
      }

      return this.renderDependentTemplate(model);
    };

    this.renderDependentSettings = ({
      dependentType,
      domain
    }) => {
      switch (dependentType) {
        case SITE_SETTINGS:
          {
            return /*#__PURE__*/_jsx(UILink, {
              external: true,
              href: Routes.settings('publishing'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey('contentSettings.publishing.allDomains')
              })
            });
          }

        case DOMAIN:
          {
            return /*#__PURE__*/_jsx(UILink, {
              external: true,
              href: Routes.systemPagesSettings(domain),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey('contentSettings.publishing.domain'),
                options: {
                  domain
                }
              })
            });
          }

        default:
          return null;
      }
    };

    this.renderDependentBlog = ({
      id,
      label
    }) => {
      return /*#__PURE__*/_jsx(UITruncateString, {
        children: /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: Routes.blogSpecificSettings(id),
          children: label
        })
      });
    };

    this.renderDependentGlobalContentPartial = ({
      id,
      label
    }) => {
      return this.renderDependentFile(TEMPLATE, {
        id,
        label
      });
    };

    this.renderDependentSalesQuoteTemplate = ({
      id,
      label
    }) => {
      return /*#__PURE__*/_jsx(ResultItem, {
        nested: true,
        title: /*#__PURE__*/_jsx(UITruncateString, {
          children: /*#__PURE__*/_jsx(UILink, {
            external: true,
            href: Routes.quoteTemplate(id),
            children: label
          })
        })
      }, id);
    };

    this.renderDependentsCategory = (category, dependentsById) => {
      const dependentsCount = Object.keys(dependentsById).length;

      if (!dependentsCount) {
        return null;
      }

      let renderDependent = () => null;

      let sortKey = 'label';

      switch (category) {
        case PUBLISHED_PAGE:
        case DRAFT_PAGE:
        case SENT_EMAIL:
        case DRAFT_EMAIL:
          {
            renderDependent = this.renderDependentContent;
            sortKey = 'name';
            break;
          }

        case CODED_FILE:
          {
            renderDependent = this.renderDependentCodedFile;
            break;
          }

        case COS_INDEX_CONNECTION_CATEGORY_CUSTOM_WIDGET:
          {
            renderDependent = this.renderDependentCustomModule;
            break;
          }

        case BLOG_POST_TEMPLATE:
        case BLOG_LISTING_TEMPLATE:
        case PAGE_TEMPLATE:
        case SYSTEM_TEMPLATE:
        case QUOTE_TEMPLATE:
        case GLOBAL_GROUP:
        case GLOBAL_CONTENT_PARTIAL:
          {
            renderDependent = this.renderDependentTemplate;
            break;
          }

        case EMAIL_TEMPLATE:
          {
            renderDependent = this.renderDependentEmailDragDropTemplate;
            break;
          }

        case CONTENT_SETTINGS:
          {
            renderDependent = this.renderDependentSettings;
            break;
          }

        case BLOG:
          renderDependent = this.renderDependentBlog;
          break;

        case SALES_QUOTE_TEMPLATE:
          renderDependent = this.renderDependentSalesQuoteTemplate;
          break;

        default:
          break;
      }

      const dependentResults = Object.keys(dependentsById).reduce((results, id) => {
        const {
          model,
          subType: dependentSubType,
          type: dependentType
        } = dependentsById[id];
        return [...results, Object.assign({}, model, {
          dependentSubType,
          dependentType
        })];
      }, []);
      const sortedDependentResults = sortBy(dependentResults, sortKey);
      return /*#__PURE__*/_jsx(UIAccordionItem, {
        className: "file-dependents__category",
        contentClassName: "file-dependents__category__content",
        title: text(i18nKey(`categories.${category}`), {
          count: dependentsCount
        }),
        children: /*#__PURE__*/_jsx(FileResultsList, {
          itemComponent: renderDependent,
          results: sortedDependentResults
        })
      }, category);
    };

    this.renderResults = () => {
      const {
        dependentsByCategory,
        dependentsCount,
        fetchDependents,
        fetchDependentsStatus,
        fileName,
        isFetchingFile
      } = this.props;

      if (fetchDependentsStatus === RequestStatus.UNINITIALIZED) {
        fetchDependents({
          fields: 'NO_META'
        });
      }

      if (fetchDependentsStatus === RequestStatus.PENDING || isFetchingFile) {
        return /*#__PURE__*/_jsx(FileResultsLoading, {});
      }

      if (fetchDependentsStatus === RequestStatus.FAILED) {
        return /*#__PURE__*/_jsx(NavMarker, {
          name: "FINDER_DEPENDENTS_ERROR",
          children: /*#__PURE__*/_jsx(FileResultsEmptyText, {
            children: /*#__PURE__*/_jsxs(UIErrorMessage, {
              illustrationProps: {
                width: 0
              },
              title: text(i18nKey('errorMessage.title')),
              children: [/*#__PURE__*/_jsx("p", {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: i18nKey('errorMessage.message'),
                  options: {
                    fileName: I18n.SafeString(fileName)
                  }
                })
              }), /*#__PURE__*/_jsxs(UIButton, {
                onClick: fetchDependents,
                use: "link",
                children: [/*#__PURE__*/_jsx(UIIcon, {
                  name: "refresh"
                }), /*#__PURE__*/_jsx(FormattedMessage, {
                  message: i18nKey('errorMessage.retryButtonText')
                })]
              })]
            })
          })
        });
      }

      if (fetchDependentsStatus === RequestStatus.SUCCEEDED && !dependentsCount) {
        return /*#__PURE__*/_jsx(FileResultsEmptyText, {
          emptyText: {
            header: text(i18nKey('results.empty.header')),
            help: text(i18nKey('results.empty.help'))
          }
        });
      }

      const categories = Object.keys(dependentsByCategory).map(__category => {
        const dependentsById = dependentsByCategory[__category];
        return this.renderDependentsCategory(__category, dependentsById);
      });
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(NavMarker, {
          name: "FINDER_DEPENDENTS"
        }), categories]
      });
    };
  }

  render() {
    const {
      dependentsCount,
      fetchDependentsStatus,
      fileName,
      hideDependents,
      fileId
    } = this.props;
    const headerText = dependentsCount === 0 ? text(i18nKey('header.none'), {
      fileName: I18n.SafeString(fileName)
    }) : text(i18nKey('header.multiple'), {
      count: dependentsCount,
      fileName: I18n.SafeString(fileName)
    });
    const isFetching = fetchDependentsStatus === RequestStatus.PENDING;
    const renderedResults = this.renderResults();
    return /*#__PURE__*/_jsx(FinderWrapper, {
      className: "file-dependents-container",
      children: /*#__PURE__*/_jsx(FileResults, Object.assign({}, this.props, {
        headerText: headerText,
        isFetching: isFetching,
        onClose: (...partialArgs) => hideDependents(fileId, ...partialArgs),
        children: renderedResults
      }))
    });
  }

}

FileDependents.propTypes = {
  dependentsByCategory: DependentsByCategoryPropType,
  dependentsCount: PropTypes.number,
  fetchDependents: PropTypes.func.isRequired,
  fetchDependentsStatus: PropTypes.oneOf(Object.values(RequestStatus || {})),
  fileName: FilePropTypes.fileName.isRequired,
  fileId: FilePropTypes.fileId.isRequired,
  hideDependents: PropTypes.func.isRequired,
  isFetchingFile: PropTypes.bool.isRequired
};
FileDependents.defaultProps = {
  dependentsByCategory: {},
  dependentsCount: 0,
  fetchDependentsStatus: RequestStatus.UNINITIALIZED,
  fileName: '',
  isFetchingFile: true
};

function mapStateToProps(state) {
  const fileId = getActiveAssetId(state);
  const file = getFile(state, {
    fileId
  });
  return {
    fileName: file && file.name,
    isFetchingFile: !file,
    fileId
  };
}

const mapDispatchToProps = {
  hideDependents: hideDependentsAction
};
export default compose(connect(mapStateToProps, mapDispatchToProps), withFileDependents)(FileDependents);